<template>
  <v-card
    class="pa-3 dashboard-card"
    :class="{ 'dashboard-card dashboard-card-dialog': isDialog }"
  >
      <v-card-title class="headline pa-0 d-flex align-center dashboard-header">
        <span class="dashboard-title">
          <v-icon class="title-icon" title="Dashboard Summary" color="primary">mdi-chart-bar</v-icon>
          Dashboard Summary
          <v-btn
            icon
            title="Refresh Data"
            small
            @click="reloadData"
            :loading="loading"
            class="refresh-btn"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </span>

        <v-spacer></v-spacer>

        <div
          style="
          display: flex;
          gap: 0;
          flex-wrap: wrap;
          flex: 0 1 auto;
          min-width: 0;
          margin-right: 50px;
        "
        >
          <v-chip
            v-for="filter in selectedFilters"
            :key="filter.type"
            color="primary"
            text-color="white"
            small
            class="mb-1"
            style="margin-right: 0; margin-left: 0"
          >
            <v-icon left small>mdi-filter</v-icon
            >{{ displayFilterText(filter) }}
            <v-icon
              small
              class="ml-2"
              color="white"
              @click="() => clearFilter(filter.type)"
              style="cursor: pointer; opacity: 0.8"
              @mouseenter="$event.target.style.opacity = 1"
              @mouseleave="$event.target.style.opacity = 0.8"
            >
              mdi-close-circle
            </v-icon>
          </v-chip>
        </div>
        <v-switch
          title="Show Inactive Technicians"
          v-model="showInactiveTechnicians"
          v-if="selectedFilters.length < 1"
          label="Show Inactive Technicians"
          inset
        ></v-switch>

        <div style="display: flex; position: absolute; right: 18px">
          <v-menu
            v-model="filterActive"
            :close-on-content-click="false"
            offset-y
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="icon-border" icon title="Select Filter" v-bind="attrs" v-on="on">
                <v-icon size="25">mdi-filter-outline</v-icon>
              </v-btn>
            </template>

            <v-card width="200">
              <v-card-title class="text-subtitle-1 pb-0">
                <b>Select Filter</b>
              </v-card-title>
              <v-card-text class="pt-0">
                <v-select
                  v-model="selectedDateType"
                  label="Select"
                  :items="dateFilterOptions"
                  item-text="label"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  @change="clearFilterSelection"
                >
                </v-select>

                <v-divider v-if="selectedDateType" class="mb-3"></v-divider>

                <v-select
                  v-if="selectedDateType"
                  v-model="selectedDateRangeText"
                  :items="
                    selectedDateType == 'dueDate'
                      ? dateRangeOptionsDue
                      : dateRangeOptions
                  "
                  item-text="label"
                  item-value="value"
                  label="Select Time Filter"
                  dense
                  outlined
                  hide-details
                  @change="onDateRangeSelected"
                ></v-select>

                <div class="mt-3" v-if="isCustomDateSelect">
                  <v-radio-group
                    v-model="selectedCustomDateType"
                    row
                    dense
                    hide-details
                    class="custom-date-radio-group"
                  >
                    <v-radio label="Is" value="is" color="primary"></v-radio>
                    <v-radio
                      label="Between"
                      value="between"
                      color="primary"
                    ></v-radio>
                  </v-radio-group>

                  <div v-if="selectedCustomDateType">
                    <label for="date">{{
                      selectedCustomDateType == "is" ? "Choose a date:" : "From"
                    }}</label>
                    <input
                      type="date"
                      id="date"
                      v-model="selectedCustomDateStart"
                      style="
                      padding: 8px;
                      border: 1px solid #ccc;
                      border-radius: 4px;
                      width: 100%;
                    "
                      @change="
                        selectedCustomDateType == 'is'
                          ? onIsCustomDateSelected()
                          : ''
                      "
                    />
                  </div>
                  <div v-if="selectedCustomDateType == 'between'">
                    <label for="date">To</label>
                    <input
                      type="date"
                      id="date"
                      v-model="selectedCustomDateEnd"
                      style="
                      padding: 8px;
                      border: 1px solid #ccc;
                      border-radius: 4px;
                      width: 100%;
                    "
                      @change="onIsCustomDateSelected"
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-menu>

          <v-btn v-if="isDialog" icon @click="closeDashboardDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <div dense class="summary-table">
        <thead>
          <tr>
            <th>
              <v-select
                v-model="selectedTechnicianNames"
                :items="
                  sortedTechnicians.filter(
                    (a) => a.technicianName !== 'Unassigned'
                  )
                "
                item-text="technicianName"
                item-value="technicianName"
                :label="
                  `${
                    selectedTechnicians &&
                    selectedTechnicians.length == sortedTechnicians.length
                      ? 'All Technicians'
                      : `Technicians (${selectedTechnicians.length})`
                  }`
                "
                multiple
                dense
                outlined
                hide-details
                @change="onTechnicianSelected"
              >
                <template fluid v-slot:prepend-item class="pt-0">
                  <div @click="toggleSelectAll" class="select-all-item">
                    <v-icon small>{{ selectAllIcon }}</v-icon>
                    <span class="ml-2">Select All</span>
                  </div>

                  <v-divider class="mt-2 mb-2"></v-divider>
                </template>
                <template v-slot:selection="{ index }">
                  <span v-if="index === 0">{{
                    selectedTechnicians &&
                    selectedTechnicians.length != sortedTechnicians.length
                      ? `Technicians (${selectedTechnicians.length || 0})`
                      : `All Technicians`
                  }}</span>
                </template>
              </v-select>
            </th>
            <!-- Columns with filter dropdown -->
            <th v-for="col in ['open', 'onHold', 'closed', 'total']" :key="col">
              <span style="font-weight: 800">{{ columnLabels[col] }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tech in (selectedFilters.length != 0 && !filterActive) ||
            showInactiveTechnicians
              ? selectedTechnicians
              : selectedTechnicians.filter(
                  (t) => t.open > 0 || t.onHold > 0 || t.technicianName == null
                )"
            :key="tech.technicianName"
          >
            <td>
              {{ tech.technicianName || "Unassigned" }}
            </td>
            <td class="stat-cell">
              <a
                @click="
                  openTechnicianRequests(tech.technicianName, 'Open', false)
                "
                :title="`${tech.technicianName || 'Unassigned'} has ${tech.open} open tickets`"
                class="stat-number"
                style="color: #1976d2"
              >
                {{ tech.open > 0 ? formatNumberWithCommas(tech.open) : "" }}
              </a>

              <div class="attention-number">
                <a
                  @click="
                    openTechnicianRequests(tech.technicianName, 'Open', true)
                  "
                  :title="`${tech.technicianName || 'Unassigned'} has ${tech.attentionOpen} open tickets needing attention`"
                  class="attention-number"
                >
                  {{
                    tech.attentionOpen > 0
                      ? formatNumberWithCommas(tech.attentionOpen)
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="
                    openTechnicianRequests(
                      tech.technicianName,
                      'On Hold',
                      false
                    )
                  "
                  :title="`${tech.technicianName || 'Unassigned'} has ${tech.onHold} on hold tickets`"
                  class="stat-number"
                  style="color: #e68210"
                >
                  {{
                    tech.onHold > 0 ? formatNumberWithCommas(tech.onHold) : ""
                  }}
                </a>
              </div>
              <div class="attention-number">
                <a
                  @click="
                    openTechnicianRequests(tech.technicianName, 'On Hold', true)
                  "
                  :title="`${tech.technicianName || 'Unassigned'} has ${tech.attentionOnHold} on hold tickets needing attention`"
                  class="attention-number"
                >
                  {{
                    tech.attentionOnHold > 0
                      ? formatNumberWithCommas(tech.attentionOnHold)
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <a
                @click="
                  openTechnicianRequests(tech.technicianName, 'Closed', false)
                "
                :title="`${tech.technicianName || 'Unassigned'} has ${tech.closed} closed tickets`"
                class="stat-number"
              >
                {{ tech.closed > 0 ? formatNumberWithCommas(tech.closed) : "" }}
              </a>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="
                    openTechnicianRequests(tech.technicianName, null, false)
                  "
                  :title="`${tech.technicianName || 'Unassigned'} has ${tech.total} total tickets`"
                  class="stat-number"
                  style="color: #170e03"
                >
                  {{ tech.total > 0 ? formatNumberWithCommas(tech.total) : "" }}
                </a>
              </div>
            </td>
          </tr>
          <tr
            v-if="
              (selectedTechnicians.length > 0 &&
                selectedTechnicians.length != sortedTechnicians.length) ||
                this.selectedFilters.length > 0
            "
            class="total-row font-weight-bold"
          >
            <td>Total selected {{ `(${selectedTechnicians.length})` }}</td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="
                    openTechnicianRequests(
                      selectedTechnicians.map((tech) => tech.technicianName),
                      'Open',
                      false
                    )
                  "
                  class="stat-number"
                  style="color: #1976d2"
                >
                  {{
                    overall.open > 0
                      ? formatNumberWithCommas(
                          selectedTechnicians.reduce(
                            (acc, tech) => acc + tech.open,
                            0
                          )
                        )
                      : ""
                  }}
                </a>
              </div>
              <div class="attention-number">
                <a
                  @click="
                    openTechnicianRequests(
                      selectedTechnicians.map((tech) => tech.technicianName),
                      'Open',
                      true
                    )
                  "
                  class="attention-number"
                >
                  {{
                    overall.attentionOpen > 0
                      ? formatNumberWithCommas(
                          selectedTechnicians.reduce(
                            (acc, tech) => acc + tech.attentionOpen,
                            0
                          )
                        )
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="
                    openTechnicianRequests(
                      selectedTechnicians.map((tech) => tech.technicianName),
                      'On Hold',
                      false
                    )
                  "
                  class="stat-number"
                  style="color: #e68210"
                >
                  {{
                    overall.onHold > 0
                      ? formatNumberWithCommas(
                          selectedTechnicians.reduce(
                            (acc, tech) => acc + tech.onHold,
                            0
                          )
                        )
                      : ""
                  }}
                </a>
              </div>
              <div class="attention-number">
                <a
                  @click="
                    openTechnicianRequests(
                      selectedTechnicians.map((tech) => tech.technicianName),
                      'On Hold',
                      true
                    )
                  "
                  class="attention-number"
                >
                  {{
                    overall.attentionOnHold > 0
                      ? formatNumberWithCommas(
                          selectedTechnicians.reduce(
                            (acc, tech) => acc + tech.attentionOnHold,
                            0
                          )
                        )
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="
                    openTechnicianRequests(
                      selectedTechnicians.map((tech) => tech.technicianName),
                      'Closed',
                      false
                    )
                  "
                  class="stat-number"
                >
                  {{
                    overall.closed > 0
                      ? formatNumberWithCommas(
                          selectedTechnicians.reduce(
                            (acc, tech) => acc + tech.closed,
                            0
                          )
                        )
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  Total
                  @click="
                    openTechnicianRequests(
                      selectedTechnicians.map((tech) => tech.technicianName),
                      null,
                      false
                    )
                  "
                  class="stat-number"
                  style="color: black"
                >
                  {{
                    overall.totalTickets > 0
                      ? formatNumberWithCommas(
                          selectedTechnicians.reduce(
                            (acc, tech) => acc + tech.total,
                            0
                          )
                        )
                      : ""
                  }}
                </a>
              </div>
            </td>
          </tr>
          <tr class="total-row font-weight-bold">
            <td>Total All Tickets</td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="openTechnicianRequests(null, 'Open', false, true)"
                  :title="`Total: ${overall.open} open tickets`"
                  class="stat-number"
                  style="color: #1976d2"
                >
                  {{
                    overall.open > 0 ? formatNumberWithCommas(overall.open) : ""
                  }}
                </a>
              </div>
              <div class="attention-number">
                <a
                  @click="openTechnicianRequests(null, 'Open', true, true)"
                  :title="`Total: ${overall.attentionOpen} open tickets needing attention`"
                  class="attention-number"
                >
                  {{
                    overall.attentionOpen > 0
                      ? formatNumberWithCommas(overall.attentionOpen)
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="openTechnicianRequests(null, 'On Hold', false, true)"
                  :title="`Total: ${overall.onHold} on hold tickets`"
                  class="stat-number"
                  style="color: #e68210"
                >
                  {{
                    overall.onHold > 0
                      ? formatNumberWithCommas(overall.onHold)
                      : ""
                  }}
                </a>
              </div>
              <div class="attention-number">
                <a
                  @click="openTechnicianRequests(null, 'On Hold', true, true)"
                  :title="`Total: ${overall.attentionOnHold} on hold tickets needing attention`"
                  class="attention-number"
                >
                  {{
                    overall.attentionOnHold > 0
                      ? formatNumberWithCommas(overall.attentionOnHold)
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="openTechnicianRequests(null, 'Closed', false, true)"
                  :title="`Total: ${overall.closed} closed tickets`"
                  class="stat-number"
                >
                  {{
                    overall.closed > 0
                      ? formatNumberWithCommas(overall.closed)
                      : ""
                  }}
                </a>
              </div>
            </td>
            <td class="stat-cell">
              <div class="stat-number">
                <a
                  @click="openTechnicianRequests(null, null, false, true)"
                  :title="`Total: ${overall.totalTickets} tickets`"
                  class="stat-number"
                  style="color: #170e03"
                >
                  {{
                    overall.totalTickets > 0
                      ? formatNumberWithCommas(overall.totalTickets)
                      : ""
                  }}
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </div>
  </v-card>
</template>

<script>
import { formatNumberWithCommas } from "@/utils/helpers";
export default {
  metaInfo() {
    if (!this.isDialog) {
      return {
        title: this.title,
      };
    } else {
      return {};
    }
  },
  props: {
    isDialog: {
      type: Boolean,
      default: false,
    },
  },
  name: "DashboardSummary",
  data() {
    return {
      title: `Dashboard Summary`,
      loading: false,
      technicians: [],
      selectedTechnicians: [],
      selectedTechnicianNames: ["Unassigned"],
      filterActive: false,
      selectedDateType: null,
      selectedDateRangeText: null,
      selectedFilter: null,
      selectedFilters: [],
      selectedDateRange: null,
      isCustomDateSelect: false,
      selectedCustomDateType: null,
      selectedCustomDateStart: null,
      selectedCustomDateEnd: null,
      showInactiveTechnicians: false,
      refreshInterval: null,

      dateRangeOptions: [
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "This Week", value: "thisWeek" },
        { label: "Last Week", value: "lastWeek" },
        { label: "This Month", value: "thisMonth" },
        { label: "Last Month", value: "lastMonth" },
        { label: "This Year", value: "thisYear" },
        { label: "Last Year", value: "lastYear" },
        { label: "Custom Date", value: "custom" },
      ],
      dateRangeOptionsDue: [
        { label: "Today", value: "today" },
        { label: "Tomorrow", value: "tomorrow" },
        { label: "Yesterday", value: "yesterday" },
        { label: "This Week", value: "thisWeek" },
        { label: "Next Week", value: "nextWeek" },
        { label: "Last Week", value: "lastWeek" },
        { label: "This Month", value: "thisMonth" },
        { label: "Next Month", value: "nextMonth" },
        { label: "Last Month", value: "lastMonth" },
        { label: "This Year", value: "thisYear" },
        { label: "Last Year", value: "lastYear" },
        { label: "Custom Date", value: "custom" },
      ],

      dateFilterOptions: [
        { label: "Create date", value: "createDate" },
        { label: "Due Date", value: "dueDate" },
        { label: "Complete Date", value: "completeDate" },
      ],

      overall: {
        totalTickets: 0,
        open: 0,
        onHold: 0,
        closed: 0,
        attentionOpen: 0,
        attentionOnHold: 0,
        attentionTotal: 0,
      },
    };
  },
  computed: {
    columnLabels() {
      return {
        open: "Open",
        onHold: "On Hold",
        closed: "Closed",
        total: "Total",
      };
    },

    sortedTechnicians() {
      return this.technicians.map((t) => {
        t.technicianName =
          t.technicianName == null ? "Unassigned" : t.technicianName;
        return t;
      });
    },

    selectAllIcon() {
      const selectableTechnicians = this.sortedTechnicians.filter(
        (a) => a.technicianName !== "Unassigned"
      );
      const allSelectableSelected = selectableTechnicians.every((t) =>
        this.selectedTechnicianNames.includes(t.technicianName)
      );
      const unassignedSelected = this.selectedTechnicianNames.includes(
        "Unassigned"
      );

      if (allSelectableSelected && unassignedSelected) {
        return "mdi-close-box";
      }
      if (this.selectedTechnicianNames.length > 0) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
  },

  async mounted() {
    await this.reloadData();
    this.clearFilterSelection();

    this.refreshInterval = setInterval(async () => {
      await this.reloadData();
    }, 300000);
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },

  methods: {
    async reloadData() {
      const savedFilters = localStorage.getItem("selected-date-filters");
      this.selectedFilters = savedFilters ? JSON.parse(savedFilters) : [];

      this.loadData();
    },

    async loadData() {
      this.loading = true;
      try {
        let url = "api/manageengine/GetDashboardSummary";

        if (this.selectedFilters && this.selectedFilters.length >= 1) {
          const filterParams = this.selectedFilters
            .map((filter) => `${filter.type}=${filter.dateRange}`)
            .join("&");

          url += `?${filterParams}`;
        }

        const response = await this.$http.get(url);
        this.technicians = response.technicians;
        this.overall = response.overall;
      } catch (e) {
        this.$notify({
          text: "Failed to load dashboard summary",
          group: "notification",
          type: "error",
        });
      } finally {
        this.loading = false;
        await this.setTechFilter();
      }
    },

    clearFilterSelection() {
      this.selectedDateRangeText = null;
      this.isCustomDateSelect = false;
      this.selectedCustomDate = null;
      this.selectedCustomDateStart = null;
      this.selectedCustomDateEnd = null;
      !this.filterActive ? (this.selectedDateType = null) : "";
      this.selectedCustomDateType = null;
    },

    changeFilter(type) {
      var prevFilterIndex = this.selectedFilters.findIndex(
        (a) => a.type === type
      );
      if (prevFilterIndex >= 0) {
        this.selectedFilters.splice(prevFilterIndex, 1);
      }
    },

    clearFilter(type) {
      this.changeFilter(type);
      localStorage.setItem(
        "selected-date-filters",
        JSON.stringify(this.selectedFilters)
      );
      this.loadData();
    },

    formatNumberWithCommas(number) {
      return formatNumberWithCommas(number);
    },

    openTechnicianRequests(technicianNames, status, filterAttention, all) {
      console.log(all);
      let technicianNamesString = Array.isArray(technicianNames)
        ? JSON.stringify(technicianNames)
        : technicianNames
        ? JSON.stringify([technicianNames])
        : "";

      localStorage.setItem("status-filter-tech", status);
      localStorage.setItem("technicians-filter-tech", technicianNamesString);
      localStorage.setItem(
        "filterTicketsNeedingAttention-tech",
        filterAttention
      );

      if (!all) {
        localStorage.setItem(
          "selected-date-filters-tech",
          localStorage.getItem("selected-date-filters")
        );
      } else {
        localStorage.setItem("selected-date-filters-tech", null);
      }

      window.open(
        this.$router.resolve({
          name: "HelpDeskTechnicianRequests",
        }).href,
        this.isDialog ? "_self" : "_blank"
      );
    },

    async setTechFilter() {
      var selectedTechnicianNames = localStorage.getItem(
        "dashboard-summary-selected-technician-names"
      );
      if (!selectedTechnicianNames) {
        this.selectedTechnicians = this.sortedTechnicians;
        return;
      }

      if (selectedTechnicianNames) {
        this.selectedTechnicianNames = JSON.parse(selectedTechnicianNames);
      }

      if (this.selectedTechnicianNames.length > 0) {
        this.selectedTechnicians = this.sortedTechnicians.filter((t) =>
          this.selectedTechnicianNames.includes(t.technicianName)
        );
      } else {
        this.selectedTechnicians = [];
      }
    },

    onTechnicianSelected(v) {
      localStorage.setItem(
        "dashboard-summary-selected-technician-names",
        JSON.stringify(v)
      );
      this.setTechFilter();
    },

    toggleSelectAll() {
      // Get only selectable technicians (excluding Unassigned from select items)
      const selectableTechnicians = this.sortedTechnicians.filter(
        (a) => a.technicianName !== "Unassigned"
      );
      const allSelectableSelected = selectableTechnicians.every((t) =>
        this.selectedTechnicianNames.includes(t.technicianName)
      );
      const unassignedSelected = this.selectedTechnicianNames.includes(
        "Unassigned"
      );

      if (allSelectableSelected && unassignedSelected) {
        this.selectedTechnicianNames = ["Unassigned"];
      } else {
        this.selectedTechnicianNames = this.sortedTechnicians.map(
          (t) => t.technicianName
        );

        if (!this.selectedTechnicianNames.includes("Unassigned")) {
          this.selectedTechnicianNames.push("Unassigned");
        }
      }
      this.onTechnicianSelected(this.selectedTechnicianNames);
    },

    closeDashboardDialog() {
      this.$emit("closeDashboardDialog");
    },

    async onDateRangeSelected() {
      if (this.selectedDateRangeText == "custom") {
        this.isCustomDateSelect = true;
        return;
      } else {
        this.getDateRangeFromOption();
      }

      await this.setSelectedFilters();
      await this.loadData();
      this.filterActive = false;
      this.clearFilterSelection();
    },

    async onIsCustomDateSelected() {
      this.selectedDateRange = this.selectedCustomDateStart;
      if (this.selectedCustomDateEnd) {
        const endDate = new Date(this.selectedCustomDateEnd);
        endDate.setDate(endDate.getDate() + 1);
        this.selectedDateRange = `${this.selectedDateRange},${
          endDate.toISOString().split("T")[0]
        }`;
      }
      this.selectedDateRangeText = this.selectedDateRange;

      await this.setSelectedFilters();
      await this.loadData();
      this.filterActive = false;
      this.clearFilterSelection();
    },

    getDateRangeFromOption() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const date = today.getDate();
      const dayOfWeek = today.getDay();

      let start = null;
      let end = null;

      switch (this.selectedDateRangeText) {
        case "today":
          start = new Date(year, month, date);
          break;

        case "tomorrow":
          start = new Date(year, month, date + 1);
          break;

        case "yesterday":
          start = new Date(year, month, date - 1);
          break;

        case "thisWeek":
          const diffToSunday = dayOfWeek === 0 ? 0 : dayOfWeek;
          start = new Date(year, month, date - diffToSunday);
          end = new Date(year, month, date - diffToSunday + 7);
          break;

        case "nextWeek":
          start = new Date(year, month, date - dayOfWeek + 7);
          end = new Date(year, month, date - dayOfWeek + 14);
          break;

        case "lastWeek":
          start = new Date(year, month, date - dayOfWeek - 7);
          end = new Date(year, month, date - dayOfWeek);
          break;

        case "thisMonth":
          start = new Date(year, month, 1);
          end = new Date(year, month + 1, 1);
          break;

        case "nextMonth":
          start = new Date(year, month + 1, 1);
          end = new Date(year, month + 2, 1);
          break;

        case "lastMonth":
          start = new Date(year, month - 1, 1);
          end = new Date(year, month, 1);
          break;

        case "thisYear":
          start = new Date(year, 0, 1);
          end = new Date(year + 1, 0, 1);
          break;

        case "lastYear":
          start = new Date(year - 1, 0, 1);
          end = new Date(year, 0, 1);
          break;

        default:
          return null;
      }

      const format = (d) => d.toISOString().split("T")[0];

      if (!end) {
        this.selectedDateRange = `${format(start)}`;
      } else {
        this.selectedDateRange = `${format(start)},${format(end)}`;
      }
    },

    async setSelectedFilters() {
      if (this.selectedFilters.some((a) => a.type == this.selectedDateType)) {
        this.changeFilter(this.selectedDateType);
      }
      this.selectedFilters.push({
        type: this.selectedDateType,
        rangeText: this.selectedDateRangeText,
        dateRange: this.selectedDateRange,
      });

      localStorage.setItem(
        "selected-date-filters",
        JSON.stringify(this.selectedFilters)
      );
    },

    displayFilterText(filter) {
      const dateFilter = this.dateFilterOptions.find(
        (item) => item.value === filter.type
      ).label;

      const dateRange = this.dateRangeOptionsDue.find(
        (item) => item.value === filter.rangeText
      )
        ? this.dateRangeOptionsDue.find(
            (item) => item.value === filter.rangeText
          ).label
        : filter.rangeText
            .split(",")
            .filter(Boolean)
            .map((a, i) =>
              i == 0
                ? new Date(`${a}T00:00:00-05:00`).toLocaleDateString()
                : new Date(`${a}`).toLocaleDateString()
            )
            .join(" - ");
      return `${dateFilter} : ${dateRange}`;
    },
  },
};
</script>

<style scoped>
.icon-border {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  transition: all 0.2s ease;
  background: #ffffff;
}

.icon-border:hover {
  border-color: #1976d2;
  background: #f5faff;
  transform: translateY(-1px);
}

/* Make the custom date radios smaller and tight, without affecting other radios */
::v-deep .custom-date-radio-group.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

::v-deep .custom-date-radio-group .v-radio {
  margin-right: 8px;
}

::v-deep .custom-date-radio-group .v-label {
  font-size: 12px;
  line-height: 1.1;
}

::v-deep .custom-date-radio-group .v-input--selection-controls__input {
  margin-right: 4px;
}

.dashboard-card {
  padding: 20px 24px !important;
  margin: 16px auto;
  display: block;
  width: fit-content;
  border: none !important;
  border-radius: 16px !important;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04) !important;
  transition: box-shadow 0.3s ease;
}

.dashboard-card:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.05) !important;
}

.dashboard-card-dialog {
  margin: 0;
  border: none !important;
  box-shadow: 0 8px 32px rgba(67, 160, 71, 0.15) !important;
  border-top: 3px solid #43a047 !important;
}

.dashboard-header {
  padding-bottom: 16px !important;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.dashboard-title {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: -0.3px;
}

.title-icon {
  margin-right: 10px;
  font-size: 26px !important;
}

.refresh-btn {
  margin-left: 8px;
  transition: transform 0.3s ease;
}

.refresh-btn:hover {
  transform: rotate(180deg);
}

.summary-table {
  width: auto;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 8px;
}

.summary-table thead tr {
  background: linear-gradient(135deg, #fafbfc 0%, #f4f6f8 100%);
}

.summary-table th {
  background-color: transparent;
  font-weight: 600;
  color: #5a6268;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 14px 18px !important;
  border-bottom: 2px solid #e9ecef;
}

.summary-table th:first-child {
  width: 380px;
  border-top-left-radius: 10px;
}

.summary-table th:last-child {
  border-top-right-radius: 10px;
}

.summary-table th:not(:first-child) {
  min-width: 150px;
  text-align: left;
}

.summary-table tbody tr {
  transition: background-color 0.15s ease;
}

.summary-table tbody tr:hover {
  background-color: #f8fafc;
}

.summary-table td {
  padding: 10px 18px !important;
  border-bottom: 1px solid #f1f3f5;
  color: #2c3e50;
  font-size: 14px;
}

.stat-cell {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.stat-number {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.5px;
  transition: opacity 0.15s ease;
  text-decoration: none !important;
}

.stat-number:hover {
  opacity: 0.75;
}

.attention-number {
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
  line-height: 1;
  transition: opacity 0.15s ease;
  text-decoration: none !important;
}

.attention-number:hover {
  opacity: 0.75;
}

.total-row {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.total-row:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.total-row td {
  border-top: 2px solid #cbd5e1;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  font-weight: 700;
  color: #1e293b;
}

.total-row td:first-child {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #475569;
}

.total-row .stat-number {
  font-size: 20px;
}

.total-row .attention-number {
  font-size: 13px;
}

.select-all-item {
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.15s ease;
}

.select-all-item:hover {
  background-color: #f1f5f9;
}

.cursor-pointer:hover {
  cursor: pointer;
}

.position-relative {
  position: relative;
}

.date-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 10;
  min-width: 180px;
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  background: transparent;
}

::v-deep .v-chip {
  border-radius: 8px !important;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2);
}
</style>
